import { adoptStyles } from '@squadlab/kernel/adopt-styles';
import { backChevron, mothershipMark } from './marks.js';

/**
 * `<squadlab-home-link>` — the operator's way back to the Command Center, rendered
 * identically on every browser-facing service (COMMAND-4, ADR 0110).
 *
 * Shape follows `docs/ui-conventions.md`: a typed `Props` interface, a pure
 * `renderHomeLink` that a React port could reuse verbatim, and a thin Custom Element
 * adapter around it. The element never navigates and never calls a port — a plain
 * left-click is handed to the host through `onActivate` so the effect can be a
 * semantic command (ADR 0032); everything else is left to the browser.
 *
 * It renders into a **shadow root** on purpose. Ten services with ten global
 * stylesheets would otherwise each drift the pattern a few pixels; the shadow
 * boundary makes "uniform" structural rather than aspirational. Theming is still
 * open through the `--oschrome-*` custom properties, which inherit across it.
 */

/** The default visible label. */
export const HOME_LINK_LABEL = 'Command Center';

/** The default accessible name — says the action *and* the destination. */
export const HOME_LINK_TITLE = 'Back to Command Center';

/** Inputs (maps 1:1 to React props). */
export interface HomeLinkProps {
  /** Absolute destination URL. */
  readonly href: string;
  /** Visible label. */
  readonly label: string;
  /** Accessible name for the link. */
  readonly title: string;
  /**
   * Called *instead of* navigating, for a plain left-click only. The host owns the
   * effect (ADR 0032). Omit it and the anchor behaves like any other link.
   */
  readonly onActivate?: (href: string, event: MouseEvent) => void;
  /**
   * Bounds the click listener's lifetime. Abort it and the handler is genuinely
   * gone, not merely detached — the disposability rule (`ui-conventions.md` rule 6)
   * applied to the one listener this component owns.
   */
  readonly signal?: AbortSignal;
}

/**
 * A click the browser would treat as "open somewhere else" — a modifier, or any
 * button but the primary one. Those must never be intercepted, or ⌘-click and
 * middle-click stop opening a new tab, which is the classic SPA link regression.
 */
function opensElsewhere(event: MouseEvent): boolean {
  return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}

/** Pure, declarative render — reusable by any framework adapter. */
export function renderHomeLink(props: HomeLinkProps): HTMLAnchorElement {
  const a = document.createElement('a');
  a.className = 'link';
  a.href = props.href;
  // `aria-label` only — a matching `title` attribute makes some screen readers
  // announce the same string twice (once as the name, once as the description),
  // and the visible label already says where this goes.
  a.setAttribute('aria-label', props.title);

  const label = document.createElement('span');
  label.className = 'label';
  // textContent, never innerHTML (ADR 0008) — the label is data as far as this
  // component is concerned, even though today every caller passes a constant.
  label.textContent = props.label;

  a.append(backChevron(), mothershipMark(), label);

  const { onActivate, signal } = props;
  if (onActivate) {
    a.addEventListener(
      'click',
      (event: MouseEvent) => {
        if (opensElsewhere(event)) return;
        event.preventDefault();
        onActivate(props.href, event);
      },
      signal ? { signal } : undefined,
    );
  }
  return a;
}

/**
 * Component styles.
 *
 * **Colour is themeable, geometry is not.** A service may re-declare any
 * `--oschrome-*` colour token on an ancestor so the chip sits in its own palette —
 * that was worth having; forcing one cyan onto ten different consoles made the chip
 * a foreign object. Size, radius and spacing are hard-coded here on purpose: when
 * they were themeable, three services shrank the type and four sharpened the corners
 * and the fleet ended up with five box widths and two shapes — read side by side it
 * was three different controls, which is precisely what this package exists to
 * prevent. Nobody gained legibility by shrinking it (they measured 12–14:1 either
 * way); it was local type-scale preference paid for in fleet coherence.
 *
 * A service overriding `--oschrome-fg` MUST clear 4.5:1 against its `--oschrome-bg`
 * (see the README) — the one override that regressed did so by mapping onto a
 * *secondary* text token, which made the way home read as a disabled control.
 */
export const HOME_LINK_CSS = `
:host {
  display: inline-flex;
  max-width: 100%;
  vertical-align: middle;

  /* The border is DERIVED from the accent, not themed separately (CC-32).
     Two reasons. It measured 1.13-1.94:1 against the backdrop on all ten services
     — including the old default — so it was decorative on every one of them while
     looking like the control's boundary; and letting each service set it produced
     the same nine-declaration token block copy-pasted six times, which is the
     duplication this package exists to remove. One accent in, a consistent edge
     out. Measured on the RENDERED border, per service: tracker 6.75, hr-engine 6.21,
     idea-engine 5.52, talent-hub 6.16, economy-radar 6.82 — against 1.1-1.9:1 before.
     0.58 alpha also cleared the 3:1 UI-component threshold but left the dimmest palette
     near the line with no headroom for a future accent tweak, so 0.68. */
  --_edge: color-mix(in srgb, var(--oschrome-accent, #5ff0ff) 68%, transparent);
  --_edge-strong: color-mix(in srgb, var(--oschrome-accent, #5ff0ff) 85%, transparent);
}
:host([hidden]) { display: none; }

.link {
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
  box-sizing: border-box;
  min-height: 32px;
  padding-block: 4px;
  padding-inline: 8px 10px;
  /* A plain rgba first so the border ALWAYS draws: putting color-mix() straight into the
     border shorthand made the whole shorthand invalid-at-computed-value-time wherever
     color-mix is unsupported (Chrome <111, Safari <16.2, Firefox <113) or the accent is
     invalid — and the longhands then fall to their initials, i.e. border-style: none,
     width 0. The chip lost its border entirely AND shrank 2px per axis. The derived edge
     is layered on top as a separate longhand, so the worst case degrades to this colour
     rather than to nothing. */
  border: 1px solid rgba(95, 240, 255, 0.68);
  border-radius: 8px;
  background: var(--oschrome-bg, rgba(8, 14, 24, 0.66));
  color: var(--oschrome-fg, #cdeaff);
  font: inherit;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  line-height: 1;
  text-transform: uppercase;
  text-decoration: none;
  white-space: nowrap;
  position: relative;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: color 140ms ease-out, border-color 140ms ease-out, background-color 140ms ease-out;
}

.link:hover,
.link:focus-visible {
  border-color: rgba(95, 240, 255, 0.85);
  background: var(--oschrome-bg-strong, rgba(12, 22, 36, 0.86));
  color: var(--oschrome-fg-strong, #ffffff);
}

/* Only once color-mix() is actually available does the border track the service's accent.
   Guarded rather than assumed: an unsupported declaration inside @supports is skipped
   wholesale, leaving the rgba above in place. */
@supports (color: color-mix(in srgb, red 50%, transparent)) {
  .link { border-color: var(--_edge); }
  .link:hover,
  .link:focus-visible { border-color: var(--_edge-strong); }
}

/* Keyboard focus must be unmistakable — this is the only global escape hatch. */
.link:focus-visible {
  outline: 2px solid var(--oschrome-accent, #5ff0ff);
  outline-offset: 2px;
}

/* "Back" points inline-start, which is right under RTL (People Engine ships Arabic).
   --flip mirrors the glyph, --nudge is the hover lean; both compose into one
   compositor-only transform so the direction logic never forks. */
.chevron {
  flex: none;
  --flip: 1;
  --nudge: 0px;
  transform: scaleX(var(--flip)) translateX(var(--nudge));
  transition: transform 140ms ease-out;
}
:host(:dir(rtl)) .chevron { --flip: -1; }
.link:hover .chevron { --nudge: -2px; }

.mark {
  flex: none;
  color: var(--oschrome-accent, #5ff0ff);
}

.label {
  overflow: hidden;
  text-overflow: ellipsis;
}

/* WCAG 2.2 SC 2.5.5 wants a 44px target; the chip is 32px tall because a taller one
   would bloat every dense HUD bar in the fleet. So the TARGET grows without the box
   growing: an invisible ::after extends the hit area to 44px. Coarse pointers only —
   that is where target size decides whether a tap lands, and on a mouse the extra
   6px above and below could steal a click from a neighbouring control for no gain. */
@media (pointer: coarse) {
  .link::after {
    content: '';
    position: absolute;
    inset-inline: 0;
    inset-block-start: 50%;
    block-size: 44px;
    translate: 0 -50%;
  }
}

/* Narrow viewports keep the mark and drop the words; the accessible name is on
   the anchor, so nothing is lost to a screen reader. */
@media (max-width: 480px) {
  .label { display: none; }
  .link { padding-inline: 8px; }
}

@media (prefers-reduced-motion: reduce) {
  .link, .chevron { transition: none; }
}
`;

/** Thin Custom Element adapter: props in, `renderHomeLink` out. */
export class HomeLinkElement extends HTMLElement {
  static readonly tagName = 'squadlab-home-link';

  #props: HomeLinkProps = { href: '', label: HOME_LINK_LABEL, title: HOME_LINK_TITLE };
  /** Bounds the listeners of the CURRENT render; aborted on re-render and teardown. */
  #live: AbortController | null = null;

  /** Inputs down. Re-renders on assignment. */
  set props(next: HomeLinkProps) {
    this.#props = next;
    this.#render();
  }

  get props(): HomeLinkProps {
    return this.#props;
  }

  /**
   * NOTE FOR HOSTS: detaching and re-attaching this element rebuilds its anchor, and
   * the browser drops keyboard focus in the process — irrecoverably, because Chromium
   * fires `focusout` *before* the detach lands and with a null `relatedTarget`, which
   * is indistinguishable from the user clicking empty space. A host that rebuilds its
   * chrome must therefore leave this element ALONE rather than re-listing it (see the
   * Talent Hub and Finance paint loops). There is no fix available from in here.
   */
  connectedCallback(): void {
    if (!this.shadowRoot) {
      const shadow = this.attachShadow({ mode: 'open' });
      adoptStyles(shadow, HOME_LINK_CSS);
    }
    this.#render();
  }

  disconnectedCallback(): void {
    this.#teardown();
  }

  #teardown(): void {
    this.#live?.abort();
    this.#live = null;
    for (const node of [...(this.shadowRoot?.children ?? [])]) node.remove();
  }

  #render(): void {
    const shadow = this.shadowRoot;
    if (!shadow || !this.#props.href) return;
    // Rebuild wholesale: the render is a pure function of props, so there is no
    // incremental-mutation path to keep correct (ui-conventions rule 3).
    this.#teardown();
    this.#live = new AbortController();
    shadow.append(renderHomeLink({ ...this.#props, signal: this.#live.signal }));
  }
}

/** Register the element. Idempotent — several services may boot it in one document. */
export function defineHomeLink(): void {
  if (!customElements.get(HomeLinkElement.tagName)) {
    customElements.define(HomeLinkElement.tagName, HomeLinkElement);
  }
}

/** Everything a host may override when mounting. */
export interface MountHomeLinkOptions {
  /**
   * Destination. Required, and always `commandCenterHref()` in practice — the
   * component stays ignorant of the catalogue so a static page can bundle it
   * without dragging Zod along (ADR 0110 §3).
   */
  readonly href: string;
  /** Visible label. Defaults to `Command Center`. */
  readonly label?: string;
  /** Accessible name. Defaults to `Back to Command Center`. */
  readonly title?: string;
  /**
   * Plain-left-click handler. Services with a CommandBus pass a callback that
   * dispatches a semantic command; services without one omit it and get ordinary
   * link navigation.
   */
  readonly onActivate?: (href: string, event: MouseEvent) => void;
}

/** A mounted link, and the way to take it back down (ui-conventions rule 6). */
export interface MountedHomeLink {
  readonly element: HomeLinkElement;
  dispose(): void;
}

/**
 * Mount the link as the **first child** of `host`.
 *
 * "First child of the service's own top-start chrome, before its brand" is the
 * placement convention (ADR 0110 §2) — a left rail and a top bar both land it in
 * the same corner of the viewport, and neither has to give up any space to an
 * overlay.
 */
export function mountHomeLink(host: ParentNode & Node, options: MountHomeLinkOptions): MountedHomeLink {
  defineHomeLink();
  const element = document.createElement(HomeLinkElement.tagName) as HomeLinkElement;
  host.insertBefore(element, host.firstChild);
  element.props = {
    href: options.href,
    label: options.label ?? HOME_LINK_LABEL,
    title: options.title ?? HOME_LINK_TITLE,
    ...(options.onActivate ? { onActivate: options.onActivate } : {}),
  };
  return {
    element,
    dispose(): void {
      // Removal fires `disconnectedCallback`, which aborts the listener and empties
      // the shadow root — nothing of the component outlives its host.
      element.remove();
    },
  };
}
